/**
 * FP3-055 — Supervisor Lens.
 *
 * Provider-facing surface for primary providers who supervise an
 * assistant inside their organization. Lists evidence rows the
 * assistant has delivered on students THIS provider supervises, so
 * the supervisor can review/sign off on assistant work without
 * having to chase it down across the assistant's caseload.
 *
 * Data path: GET /api/goal-evidence?supervised_by_me=true. The
 * backend enriches rows with student_name / delivered_by_name /
 * goal_text. Server-side scope prevents cross-supervisor leakage:
 * an explicit ?supervised_by=<other_pid> returns 403 for
 * non-admin callers.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck, CaretRight, Eye, Warning, ChartLineUp, Student as StudentIcon,
  CheckCircle, ArrowUUpLeft,
} from "@phosphor-icons/react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
// FP3-072 — Contextual Help Center deep-link.
import HelpLink from "@/components/help/HelpLink";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch {
    return iso;
  }
}

function fmtAckTs(iso) {
  // FP3-055 follow-up — ack timestamps come back as full ISO so we
  // need a slightly different formatter that handles trailing "Z".
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function pct(n, d) {
  if (!d) return null;
  return Math.round((100 * n) / d);
}

export default function SupervisorLens() {
  const { user } = useAuth();
  const role = (user?.permission_role || "").toLowerCase();
  const legacyAssistant = (user?.provider_role_type || "").toLowerCase() === "assistant";
  // FP3-055 — Hard gate: only primary providers can view the lens.
  // Assistants (current and legacy `provider_role_type=assistant`)
  // and admin family land on the access-denied state and never
  // call the API.
  const canView = role === "provider" && !legacyAssistant;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // FP3-055 follow-up — Pending / Acknowledged / All filter chip.
  // Default to "pending" so the lens opens as a working queue.
  const [statusFilter, setStatusFilter] = useState("pending");
  // Track per-row in-flight ack mutation so the button can show a
  // disabled state instead of double-firing.
  const [busyRowId, setBusyRowId] = useState("");
  // FP3-069 follow-up #14 — Supervised graph (assistants + students)
  // loaded from `/api/supervisor-lens/context` so the summary cards
  // stay accurate even when zero evidence has been logged yet.
  const [context, setContext] = useState({
    supervised_assistants: [],
    supervised_students: [],
  });

  const load = async () => {
    if (!canView) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const [ctxR, evR] = await Promise.all([
        api.getSupervisorLensContext().catch(() => ({
          data: { supervised_assistants: [], supervised_students: [] },
        })),
        api.listGoalEvidence({ supervised_by_me: true, limit: 500 }),
      ]);
      setContext(ctxR.data || { supervised_assistants: [], supervised_students: [] });
      setRows(Array.isArray(evR.data) ? evR.data : []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) setError("Please sign in to view the supervisor lens.");
      else if (status === 403) setError("You do not have access to the supervisor lens.");
      else setError(err?.response?.data?.detail || "Could not load supervised work.");
      toast.error(err?.response?.data?.detail || "Could not load supervised work.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  // FP3-055 follow-up — single ack toggle. POSTs when not acked,
  // DELETEs when already acked. Optimistic local update so the UI
  // stays responsive; on error we toast and reload to re-sync.
  const toggleAck = async (row) => {
    if (busyRowId) return;
    setBusyRowId(row.id);
    const alreadyAcked = !!row.supervisor_ack_at;
    try {
      if (alreadyAcked) {
        await api.unacknowledgeGoalEvidence(row.id);
        setRows((prev) => prev.map((r) =>
          r.id === row.id
            ? { ...r, supervisor_ack_at: "", supervisor_ack_by_provider_id: "" }
            : r
        ));
        toast.success("Acknowledgement cleared");
      } else {
        const resp = await api.acknowledgeGoalEvidence(row.id);
        const ackAt = resp?.data?.supervisor_ack_at || new Date().toISOString();
        const ackBy = resp?.data?.supervisor_ack_by_provider_id || "";
        setRows((prev) => prev.map((r) =>
          r.id === row.id
            ? { ...r, supervisor_ack_at: ackAt, supervisor_ack_by_provider_id: ackBy }
            : r
        ));
        toast.success("Acknowledged");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update acknowledgement");
      load();
    } finally {
      setBusyRowId("");
    }
  };

  // FP3-055 follow-up — derive Pending / Acknowledged counts up front
  // so the filter pills can show "Pending · N" without re-scanning
  // on every render of the group list.
  const counts = useMemo(() => {
    let pending = 0;
    let acked = 0;
    for (const r of rows) {
      if (r.supervisor_ack_at) acked += 1;
      else pending += 1;
    }
    return { pending, acked, all: rows.length };
  }, [rows]);

  // Apply the filter before grouping so groups with no matching
  // rows fall away.
  const filteredRows = useMemo(() => {
    if (statusFilter === "pending") {
      return rows.filter((r) => !r.supervisor_ack_at);
    }
    if (statusFilter === "acknowledged") {
      return rows.filter((r) => !!r.supervisor_ack_at);
    }
    return rows;
  }, [rows, statusFilter]);

  // Group by student for a tidy stack of cards.
  const groups = useMemo(() => {
    const m = new Map();
    for (const r of filteredRows) {
      const sid = r.student_id;
      if (!m.has(sid)) {
        m.set(sid, {
          student_id: sid,
          student_name: r.student_name || sid,
          rows: [],
        });
      }
      m.get(sid).rows.push(r);
    }
    return Array.from(m.values());
  }, [filteredRows]);

  const totalStudents = groups.length;
  const totalRows = filteredRows.length;
  // Distinct assistants who delivered the supervised work.
  const distinctAssistants = useMemo(() => {
    const seen = new Set();
    for (const r of filteredRows) {
      const key = r.delivered_by_name || r.provider_id;
      if (key) seen.add(key);
    }
    return seen.size;
  }, [filteredRows]);
  // FP3-069 follow-up #14 — Prefer the context-driven counts so the
  // summary cards stay accurate even when no evidence exists yet.
  // The evidence-derived counts above stay as a fallback (and still
  // drive the Pending/Acknowledged filter rendering below).
  const supervisedStudentCount = Math.max(
    totalStudents,
    (context.supervised_students || []).length,
  );
  const supervisedAssistantCount = Math.max(
    distinctAssistants,
    (context.supervised_assistants || []).length,
  );

  if (!canView) {
    return (
      <div className="p-8 max-w-3xl">
        <Card className="border-[#E5E3DB]" data-testid="supervisor-lens-denied">
          <CardContent className="p-6">
            <p className="text-[13.5px] text-[#1F2937] flex items-center gap-1.5">
              <Warning size={16} weight="duotone" className="text-[#A8453A]" />
              The supervisor lens is only available to primary providers
              who supervise an assistant. Assistants and administrators
              don't have a supervised queue.
            </p>
            <div className="mt-3">
              <Link to="/" className="text-[12.5px] text-[#1E3F33] underline">
                Back to Today
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="p-6 lg:p-8 max-w-5xl space-y-5"
      data-testid="supervisor-lens-page"
    >
      <header>
        <h1
          className="text-2xl font-semibold text-[#1F2937] heading-font flex items-center gap-2"
          data-testid="supervisor-lens-title"
        >
          <ShieldCheck size={22} weight="duotone" className="text-[#1E3F33]" />
          Supervisor Lens
        </h1>
        <p className="text-[12.5px] text-[#6B7280] mt-1">
          Assistant-delivered evidence on students you supervise. You see
          ONLY rows where you are the supervising provider — other primary
          providers' supervision queues stay private.
        </p>
        {/* FP3-072 — Contextual help link to the Assistants /
            SLPAs section of the Help Center. */}
        <div className="mt-1.5">
          <HelpLink
            articleId="supervisor-lens"
            label="Learn how Supervisor Lens works"
            testId="supervisor-lens-help"
          />
        </div>
      </header>

      {/* Summary band */}
      <div className="grid grid-cols-4 gap-3" data-testid="supervisor-lens-summary">
        <SummaryCard
          label="Students supervised"
          value={supervisedStudentCount}
          testid="supervisor-lens-summary-students"
          icon={<StudentIcon size={14} weight="duotone" />}
        />
        <SummaryCard
          label="Pending review"
          value={counts.pending}
          testid="supervisor-lens-summary-pending"
          icon={<ShieldCheck size={14} weight="duotone" />}
          accent={counts.pending > 0 ? "warn" : "calm"}
        />
        <SummaryCard
          label="Evidence rows"
          value={totalRows}
          testid="supervisor-lens-summary-rows"
          icon={<ChartLineUp size={14} weight="duotone" />}
        />
        <SummaryCard
          label="Assistants delivering"
          value={supervisedAssistantCount}
          testid="supervisor-lens-summary-assistants"
          icon={<ShieldCheck size={14} weight="duotone" />}
        />
      </div>

      {/* FP3-069 follow-up #14 — No-evidence-yet banner. Renders when
          the supervision relationship is active (≥1 supervised student
          per context) but the assistant hasn't logged any evidence
          yet. Without this the page rendered the generic "no rows
          match" empty state and the supervisor couldn't tell whether
          they had no assistants at all OR just hadn't received any
          evidence yet. */}
      {!loading && !error && rows.length === 0 && (context.supervised_students || []).length > 0 && (
        <div
          data-testid="supervisor-lens-no-evidence-yet"
          className="rounded-lg border border-[#E5E3DB] bg-white p-5 text-[13px] text-[#1F2937] space-y-2"
        >
          <p className="font-medium flex items-center gap-1.5">
            <ShieldCheck size={14} weight="duotone" className="text-[#1E3F33]" />
            You supervise {(context.supervised_students || []).length} student
            {(context.supervised_students || []).length === 1 ? "" : "s"} across {(context.supervised_assistants || []).length} assistant
            {(context.supervised_assistants || []).length === 1 ? "" : "s"}.
          </p>
          <p className="text-[12px] text-[#6B7280] leading-snug">
            No assistant-delivered evidence has been logged yet. Once your
            supervised assistant
            {(context.supervised_assistants || []).length === 1 ? " files" : "s file"}{" "}
            evidence (sessions, narrative notes, percent-trial points), it'll
            show up here for review.
          </p>
          <details className="text-[11.5px] text-[#6B7280]">
            <summary
              data-testid="supervisor-lens-no-evidence-supervised-toggle"
              className="cursor-pointer text-[#3F4756] hover:text-[#1E3F33]"
            >
              Who am I supervising?
            </summary>
            <ul
              data-testid="supervisor-lens-no-evidence-supervised-list"
              className="mt-1.5 ml-4 list-disc space-y-0.5"
            >
              {(context.supervised_students || []).slice(0, 50).map((s) => (
                <li key={s.student_id}>
                  {s.name}
                  {s.assistant_provider_names?.length > 0 && (
                    <span className="text-[#9B9890]"> · {s.assistant_provider_names.join(" + ")}</span>
                  )}
                </li>
              ))}
            </ul>
          </details>
        </div>
      )}

      {/* FP3-055 follow-up — Pending / Acknowledged / All filter pills.
          Lens opens in "pending" mode so it reads like a queue; pills
          carry live counts that recompute as the supervisor clicks
          through ack actions. */}
      {!loading && !error && rows.length > 0 && (
        <div
          className="flex items-center gap-2 flex-wrap"
          data-testid="supervisor-lens-filter-strip"
        >
          {[
            { key: "pending", label: `Pending · ${counts.pending}` },
            { key: "acknowledged", label: `Acknowledged · ${counts.acked}` },
            { key: "all", label: `All · ${counts.all}` },
          ].map((opt) => (
            <button
              type="button"
              key={opt.key}
              data-testid={`supervisor-lens-filter-${opt.key}`}
              onClick={() => setStatusFilter(opt.key)}
              className={`text-[11.5px] uppercase tracking-[0.06em] font-medium rounded-full px-3 py-1 border transition-colors ${
                statusFilter === opt.key
                  ? "bg-[#1E3F33] border-[#1E3F33] text-white"
                  : "bg-white border-[#E5E3DB] text-[#3F4756] hover:bg-[#F3F1EC]"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {loading && (
        <div
          className="text-[13px] text-[#6B7280] py-10 text-center"
          data-testid="supervisor-lens-loading"
        >
          Loading supervised work…
        </div>
      )}

      {!loading && error && (
        <Card className="border-[#E5E3DB]" data-testid="supervisor-lens-error">
          <CardContent className="p-6 text-[13px] text-[#7A3A2C]">
            <Warning size={14} weight="duotone" className="inline mr-1 mb-0.5" />
            {error}
          </CardContent>
        </Card>
      )}

      {!loading && !error && rows.length === 0 && (
        <Card className="border-dashed border-[#E5E3DB]" data-testid="supervisor-lens-empty">
          <CardContent className="p-8 text-center">
            <p className="text-[13.5px] text-[#1F2937] mb-1">
              No assistant-delivered work yet.
            </p>
            <p className="text-[12.5px] text-[#6B7280]">
              When an assistant logs evidence on a student you supervise,
              it will appear here grouped by student.
            </p>
          </CardContent>
        </Card>
      )}

      {/* FP3-055 follow-up — filter results in zero rows but the
          supervisor still has supervised work overall. Calmer
          empty state so the queue feels finished, not broken. */}
      {!loading && !error && rows.length > 0 && filteredRows.length === 0 && (
        <Card className="border-dashed border-[#E5E3DB]" data-testid="supervisor-lens-filter-empty">
          <CardContent className="p-8 text-center">
            {statusFilter === "pending" ? (
              <>
                <p className="text-[13.5px] text-[#1F2937] mb-1 inline-flex items-center gap-1.5">
                  <CheckCircle size={15} weight="duotone" className="text-[#1E3F33]" />
                  All caught up.
                </p>
                <p className="text-[12.5px] text-[#6B7280]">
                  Every assistant-delivered row on your queue has been
                  acknowledged.
                </p>
              </>
            ) : (
              <p className="text-[12.5px] text-[#6B7280]">
                No rows match this filter yet.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {!loading && !error && groups.length > 0 && (
        <div className="space-y-4" data-testid="supervisor-lens-groups">
          {groups.map((g) => (
            <Card
              key={g.student_id}
              className="border-[#E5E3DB]"
              data-testid={`supervisor-lens-student-${g.student_id}`}
            >
              <CardContent className="p-0">
                <div className="px-5 py-3 flex items-center justify-between border-b border-[#F1F2F4]">
                  <div className="flex items-center gap-2 min-w-0">
                    <h3
                      className="text-[13.5px] font-semibold text-[#1F2937] truncate"
                      data-testid={`supervisor-lens-student-name-${g.student_id}`}
                    >
                      {g.student_name}
                    </h3>
                    <span className="text-[11px] text-[#6B7280]">
                      · {g.rows.length} row{g.rows.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <Link
                    to={`/students/${g.student_id}`}
                    data-testid={`supervisor-lens-open-student-${g.student_id}`}
                    className="text-[11.5px] text-[#1E3F33] inline-flex items-center gap-0.5 hover:underline"
                  >
                    <Eye size={11} /> Open student
                    <CaretRight size={10} />
                  </Link>
                </div>
                <ul className="divide-y divide-[#F1F2F4]">
                  {g.rows.map((r) => {
                    const p = pct(r.numerator, r.denominator);
                    const acked = !!r.supervisor_ack_at;
                    const busy = busyRowId === r.id;
                    return (
                      <li
                        key={r.id}
                        className={`px-5 py-3 ${acked ? "bg-[#FBFAF6]" : ""}`}
                        data-testid={`supervisor-lens-row-${r.id}`}
                        data-ack-state={acked ? "acknowledged" : "pending"}
                      >
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="min-w-0 flex-1">
                            <p
                              className="text-[12.5px] text-[#1F2937] line-clamp-2"
                              data-testid={`supervisor-lens-row-goal-${r.id}`}
                            >
                              {r.goal_text || (r.goal_id ? `Goal ${r.goal_id.slice(0, 6)}…` : "Untitled goal")}
                            </p>
                            <p
                              className="text-[11px] text-[#6B7280] mt-0.5 flex items-center gap-1 flex-wrap"
                              data-testid={`supervisor-lens-row-meta-${r.id}`}
                            >
                              <span>{fmtDate(r.date)}</span>
                              <span>·</span>
                              <span>
                                Delivered by{" "}
                                <span
                                  className="font-medium text-[#3F4756]"
                                  data-testid={`supervisor-lens-row-delivered-by-${r.id}`}
                                >
                                  {r.delivered_by_name || r.provider_id?.slice(0, 6) || "—"}
                                </span>
                                {r.delivered_by_role ? ` · ${r.delivered_by_role}` : ""}
                              </span>
                              {r.goal_domain && (
                                <>
                                  <span>·</span>
                                  <span className="uppercase tracking-wide text-[10px] text-[#5C7088]">
                                    {r.goal_domain.replace(/_/g, " ")}
                                  </span>
                                </>
                              )}
                            </p>
                            {r.notes && (
                              <p
                                className="text-[11.5px] text-[#3F4756] mt-1 italic"
                                data-testid={`supervisor-lens-row-notes-${r.id}`}
                              >
                                “{r.notes}”
                              </p>
                            )}
                          </div>
                          <div className="text-right shrink-0">
                            <p
                              className="text-[14px] font-semibold text-[#1E3F33] tabular-nums"
                              data-testid={`supervisor-lens-row-score-${r.id}`}
                            >
                              {r.numerator}/{r.denominator}
                            </p>
                            {p !== null && (
                              <p className="text-[10.5px] text-[#6B7280] tabular-nums">
                                {p}%
                              </p>
                            )}
                          </div>
                        </div>
                        {/* FP3-055 follow-up — Acknowledge / Acknowledged
                            footer. Single click stamps supervisor_ack_at;
                            the Undo affordance under the filled chip
                            clears the stamp. */}
                        <div className="mt-2 flex items-center justify-end gap-2">
                          {acked ? (
                            <>
                              <span
                                className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.08em] font-semibold rounded-full px-2 py-0.5 bg-[#E8F0EB] text-[#1E3F33]"
                                data-testid={`supervisor-lens-row-ack-state-${r.id}`}
                              >
                                <CheckCircle size={11} weight="fill" />
                                Acknowledged · {fmtAckTs(r.supervisor_ack_at)}
                              </span>
                              <button
                                type="button"
                                onClick={() => toggleAck(r)}
                                disabled={busy}
                                data-testid={`supervisor-lens-row-unack-${r.id}`}
                                className="inline-flex items-center gap-1 text-[10.5px] text-[#6B7280] hover:text-[#1E3F33] disabled:opacity-50"
                                title="Undo acknowledgement"
                              >
                                <ArrowUUpLeft size={11} /> Undo
                              </button>
                            </>
                          ) : (
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => toggleAck(r)}
                              disabled={busy}
                              data-testid={`supervisor-lens-row-ack-${r.id}`}
                              className="h-7 text-[11.5px] bg-[#1E3F33] hover:bg-[#173127] disabled:bg-[#1E3F33]/60 text-white inline-flex items-center gap-1"
                            >
                              <CheckCircle size={11} weight="bold" />
                              {busy ? "Acknowledging…" : "Acknowledge"}
                            </Button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!loading && !error && (
        <div className="pt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={load}
            data-testid="supervisor-lens-refresh"
            className="text-[12px] border-[#E5E3DB] text-[#3F4756]"
          >
            Refresh
          </Button>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, testid, icon, accent }) {
  // FP3-055 follow-up — subtle accent so the "Pending review" card
  // can warn the supervisor when the queue isn't empty without
  // shouting.
  const valueColor = accent === "warn" && value > 0
    ? "text-[#7A5B1F]"
    : "text-[#1F2937]";
  return (
    <Card className="border-[#E5E3DB]" data-testid={testid}>
      <CardContent className="p-4">
        <p className="text-[10.5px] uppercase tracking-[0.08em] font-medium text-[#6B7280] flex items-center gap-1">
          {icon}
          {label}
        </p>
        <p className={`text-[22px] font-semibold tabular-nums mt-0.5 ${valueColor}`}>
          {value}
        </p>
      </CardContent>
    </Card>
  );
}
